
import type { TVirtResponseError } from "./response-error.js";



// Auto-pagination (syntax fixed by Luca, 2026-07-10): `next()` yields ONE item per
// call, null at exhaustion; `for await` is a trivial wrapper. Concurrent next() calls
// are serialized on an internal promise chain so they never double-fetch a page.

export type PaginatedFetch<T> = (params: { cursor: string | null }) => Promise<{ items: T[]; nextCursor: string | null; stale?: boolean; stateAsOf?: string | null; unreadable?: TVirtResponseError[] }>;

export interface PageIterator<T> extends AsyncIterable<T> {
    next(): Promise<T | null>;
    // The page envelope's freshness marker (Fase 7; cluster-first §6): true once any
    // fetched page was served from the stale replica fallback — undefined before the
    // first fetch. Read it AFTER iterating to label the collected list.
    stale(): boolean | undefined;
    // The AGE of the observed state on a stale drain (T324-P6, 2026-09-04): the
    // envelope's `stateAsOf` — WHEN the state half of the items was last taken
    // from the store — folded across the pages to the OLDEST one (the honest
    // bound: the list is at least that old). `null` = a page said the state
    // replica never reached the store (this node's own observations only);
    // undefined = no drained page carried the field (fresh drain, or an
    // envelope that does not publish it yet). Read it AFTER iterating.
    stateAsOf(): string | null | undefined;
    // The items skipped because this client cannot read them (round «errori
    // onesti», 2026-08-03), accumulated across every fetched page — same idiom
    // as `stale()`: read it AFTER iterating to tell the operator that the list
    // is short, and by how much and why. Empty on a list that validated whole.
    unreadable(): TVirtResponseError[];
}



export function genPageIterator<T>(fn: PaginatedFetch<T>): PageIterator<T> {
    let started = false;
    let ended = false;
    let sawStale: boolean | undefined;
    let oldestStateAsOf: string | null | undefined;
    const skipped: TVirtResponseError[] = [];

    const foldStateAsOf = (pageStateAsOf: string | null | undefined): void => {
        if (pageStateAsOf === undefined || oldestStateAsOf === null) {
            return;
        }
        if (pageStateAsOf === null || oldestStateAsOf === undefined || Date.parse(pageStateAsOf) < Date.parse(oldestStateAsOf)) {
            oldestStateAsOf = pageStateAsOf;
        }
    };
    let cache: T[] = [];
    let lastCursor: string | null = null;
    let chain: Promise<T | null> = Promise.resolve(null);

    const advance = async (): Promise<T | null> => {
        if (ended) {
            return null;
        }
        if (cache.length === 0) {
            if (started && lastCursor === null) {
                ended = true;
                return null;
            }
            const out = await fn({ cursor: lastCursor });
            started = true;
            sawStale = (sawStale ?? false) || out.stale === true;
            foldStateAsOf(out.stateAsOf);
            skipped.push(...out.unreadable ?? []);
            cache = out.items;
            lastCursor = out.nextCursor;
            if (cache.length === 0) {
                // The cursor engine never emits nextCursor on an empty page, so an
                // empty fetch is always a legitimate end (offset past the tail after
                // concurrent deletions included).
                ended = true;
                return null;
            }
        }
        return cache.shift() ?? null;    // resources are objects, never null
    };

    const next = (): Promise<T | null> => {
        chain = chain.then(advance, advance);
        return chain;
    };

    return {
        next,
        stale: (): boolean | undefined => sawStale,
        stateAsOf: (): string | null | undefined => oldestStateAsOf,
        unreadable: (): TVirtResponseError[] => [...skipped],
        async *[Symbol.asyncIterator](): AsyncGenerator<T> {
            for (let item = await next(); item !== null; item = await next()) {
                yield item;
            }
        },
    };
}
